import { Autocomplete, TextField, Chip } from "@mui/material";
import { useState, UIEvent } from "react";
import { useInfiniteAutocomplete } from "@/app/hooks/useInfiniteAutocomplete";
import { PaginatedMultiAutocompleteProps } from "@/app/interfaces/props/PaginatedMultiAutocompleteProps";
import { Option } from "@/app/interfaces/Option";

const PaginatedMultiAutocomplete = ({
  label,
  name,
  value,
  queryKey,
  fetchFn,
  onChange,
  size = "medium",
}: PaginatedMultiAutocompleteProps & {
  onOptionsChange?: (options: Option[]) => void;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  const {
    options,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isDebouncing,
  } = useInfiniteAutocomplete(queryKey, fetchFn, inputValue, open);

  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    const nearBottom =
      listboxNode.scrollHeight -
        listboxNode.scrollTop -
        listboxNode.clientHeight <
      50;

    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Autocomplete<Option, true, false, false>
      multiple
      size={size}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      getOptionLabel={(opt) => opt.text}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={(_, val) => setInputValue(val)}
      filterOptions={(x) => x}
      loading={isLoading || isDebouncing || isFetching || isFetchingNextPage}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option.text}
              {...tagProps}
              sx={{
                backgroundColor: "#e0e0e0",
                fontSize: "13px",
                color: "#333",
                borderRadius: "16px",
                margin: "1px !important",
                height: "22px",
                padding: "0px !important",
                fontWeight: 500,
                "& .MuiChip-deleteIcon": {
                  color: "#666",
                  fontSize: "14px",
                  "&:hover": {
                    color: "#000",
                  },
                },
              }}
            />
          );
        })
      }
      ListboxProps={{
        onScroll: handleScroll,
        sx: { maxHeight: 250, overflow: "auto" },
      }}
      renderOption={(props, option) => <li {...props}>{option.text}</li>}
      renderInput={(params) => (
        <TextField
          sx={{
            borderRadius: "4px",
            padding: "0px !important",
            width: "100%",
            color: "#000",
            alignItems: "baseline",
            "& .MuiOutlinedInput-root": {
              padding: "3px !important",
              "& fieldset": {
                borderColor: "#d9d9d9",
                borderRadius: "4px",
              },
            },
          }}
          {...params}
          name={name}
          label={label}
          variant="outlined"
          InputProps={{
            disableUnderline: true,
            ...params.InputProps,
            endAdornment: <>{params.InputProps.endAdornment}</>,
          }}
        />
      )}
    />
  );
};

export default PaginatedMultiAutocomplete;
